import { useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import getShopifyData from 'lib/shopify/getShopifyData';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import { keys, get, set } from 'lib/utils/sessionStorage';
import styles from './bundleBuilder.module.scss';
import Step from './step';
import SelectedPopup from './selectedPopup';

const PageBundleBuilder = ({ data, locale, resources }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shopifyData, setShopifyData] = useState({});

  useEffect(() => {
    // do your side effects here
    const allProductIds = data.steps.reduce(
      (acc, step) => [
        ...acc,
        ...(step.products?.map(product =>
          getShopifyGid('Product', product.shopifyData.variants?.[0]?.product_id)
        ) || []),
      ],
      []
    );

    const fetchAllProducts = async () => {
      const response = await getShopifyData({ productIds: allProductIds, locale });
      setShopifyData(response);
    };
    fetchAllProducts();
  }, [data.steps, locale]);

  const saveProductsToStorage = products => {
    // save selected products to session storage
    const selectedProductsIds = products?.length ? products.map(({ product }) => product.id) : [];
    set(keys.BUNDLE_BUILDER_PRODUCTS, selectedProductsIds);
  };

  const handleSelect = (product, productShopifyData, index) => {
    const newProducts = [...selectedProducts, { step: index, product, productShopifyData }];
    setSelectedProducts(newProducts);
    saveProductsToStorage(newProducts);
  };

  const handleRemove = index => {
    const newProducts = selectedProducts.splice(0, index);
    setSelectedProducts(newProducts);
    saveProductsToStorage(newProducts);
  };

  useEffect(() => {
    if (!shopifyData.length) return;

    const storageProducts = get(keys.BUNDLE_BUILDER_PRODUCTS);
    const selectedProductsIds = storageProducts ? storageProducts.split(',') : [];

    // restore selected products from locale storage
    const restoredProducts = data.steps.reduce((acc, step, index) => {
      const selectedProduct = selectedProductsIds[index];
      if (selectedProduct) {
        const product = step.products.find(p => p.id === selectedProduct);
        if (!product) return acc;
        const productShopifyData = shopifyData.find(
          p =>
            p.productId === getShopifyGid('Product', product.shopifyData.variants?.[0]?.product_id)
        );
        return [...acc, { step: index, product, productShopifyData }];
      }
      return acc;
    }, []);

    // if storage data is not valid, clear storage
    if (restoredProducts.length !== selectedProductsIds.length) {
      set(keys.BUNDLE_BUILDER_PRODUCTS, '');
      return;
    }

    setSelectedProducts(restoredProducts);
  }, [data.steps, shopifyData]);

  return (
    <section className={styles.bundleBuilderPage}>
      <h1 className={styles.title}>{data.title}</h1>
      <RichText text={data?.subtitle?.json} className={styles.subtitle} />

      <div className={styles.steps}>
        {data.steps.map((step, index) => (
          <Step
            data={step}
            visible={selectedProducts.length > index - 1}
            resources={resources}
            shopifyData={shopifyData}
            pageTitle={data.title}
            selectedProducts={selectedProducts}
            selectCallback={(product, productShopifyData) =>
              handleSelect(product, productShopifyData, index)
            }
            stepIndex={index}
            removeCallback={handleRemove}
            addButtonText={data.addButtonText}
            removeButtonText={data.removeButtonText}
          />
        ))}
      </div>

      <SelectedPopup
        message={data.selectedPopupMessage}
        selectedProducts={selectedProducts}
        itemsCount={data.steps.length}
        removeCallback={handleRemove}
        resources={resources}
        locale={locale}
        discount={data.discount}
        itemMessage={data.cartItemMessage}
      />
    </section>
  );
};

export default PageBundleBuilder;
